import { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Camera, Upload, Trash2, Loader2, Circle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import Avatar from "./Avatar";

const STATUSES = [
  { v: "available", label: "Disponible", color: "#10b981" },
  { v: "busy", label: "Occupé", color: "#f59e0b" },
  { v: "away", label: "Absent", color: "#94a3b8" },
];

async function croppedBase64(src, area) {
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return canvas.toDataURL("image/jpeg", 0.85);
}

export default function ProfilePhotoDialog({ open, onOpenChange }) {
  const { user, refresh } = useAuth();
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Choisissez une image."); return; }
    const r = new FileReader();
    r.onload = () => { setSrc(r.result); setZoom(1); setCrop({ x: 0, y: 0 }); };
    r.readAsDataURL(f);
  };

  const onCropComplete = useCallback((_, px) => setArea(px), []);

  const save = async () => {
    if (!src || !area) { toast.error("Choisissez et cadrez une photo."); return; }
    setSaving(true);
    try {
      const b64 = await croppedBase64(src, area);
      await api.post("/planning/avatar", { image_base64: b64, content_type: "image/jpeg" });
      await refresh();
      toast.success("Photo de profil mise à jour.");
      setSrc(null);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setSaving(false);
  };

  const removePhoto = async () => {
    setSaving(true);
    try { await api.delete("/planning/avatar"); await refresh(); toast.success("Photo supprimée."); setSrc(null); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setSaving(false);
  };

  const setStatus = async (v) => {
    try { await api.post("/planning/presence/status", { status: v }); await refresh(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const curStatus = user?.presence_status || "available";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-photo-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Camera className="h-5 w-5" />Photo de profil</DialogTitle></DialogHeader>
        {!src && (
          <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3" data-testid="presence-status-picker">
            <div className="text-xs font-semibold text-slate-500 mb-2">Mon statut</div>
            <div className="flex gap-2">
              {STATUSES.map((s) => (
                <button key={s.v} onClick={() => setStatus(s.v)} data-testid={`status-${s.v}`}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition ring-1 ${curStatus === s.v ? "ring-transparent text-white" : "ring-slate-200 text-slate-600 bg-white hover:bg-slate-100"}`}
                  style={curStatus === s.v ? { background: s.color } : {}}>
                  <Circle className="h-2.5 w-2.5 fill-current" style={curStatus === s.v ? {} : { color: s.color }} />{s.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {!src ? (
          <div className="flex flex-col items-center gap-4 py-4">
            <Avatar url={user?.avatar_url} name={user?.name} size={96} />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-file-input" />
            <Button onClick={() => fileRef.current?.click()} data-testid="profile-choose-btn" className="bg-sky-500 hover:bg-sky-600 text-white">
              <Upload className="h-4 w-4 mr-2" />Choisir une photo
            </Button>
            {user?.avatar_url && (
              <button onClick={removePhoto} data-testid="profile-remove-btn" className="text-sm text-red-500 hover:text-red-600 inline-flex items-center gap-1">
                <Trash2 className="h-4 w-4" />Retirer la photo actuelle
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative w-full h-64 bg-slate-900 rounded-xl overflow-hidden">
              <Cropper image={src} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
                onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
            </div>
            <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="profile-zoom" className="w-full accent-sky-500" />
            <p className="text-xs text-slate-400 text-center">Déplacez et zoomez pour cadrer votre visage.</p>
          </div>
        )}
        <DialogFooter>
          {src && <Button variant="ghost" onClick={() => setSrc(null)}>Changer</Button>}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          {src && (
            <Button onClick={save} disabled={saving} data-testid="profile-save-btn" className="bg-sky-500 hover:bg-sky-600 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
